import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { MessageSquare, ChevronRight } from 'lucide-react';
import { serviceRequestAPI } from '../../services/api';
import ServiceChat from '../../components/services/ServiceChat';
import { useInteractionMode } from '../../hooks/useInteractionMode';

const SERVICE_TYPE_LABELS = {
  PROFILE_ASSESSMENT:       'Profile Assessment',
  UNIVERSITY_SHORTLISTING:  'Preapplication Support',
  APPLICATION_ASSISTANCE:   'Apply University',
  VISA_GUIDANCE:            'Visa & Interview Support',
  SCHOLARSHIP_SEARCH:       'Ticket & Travel Support',
  LOAN_ASSISTANCE:          'Education Loan',
  ACCOMMODATION_HELP:       'Find Accommodation',
  PRE_DEPARTURE_ORIENTATION:'Find Jobs Abroad',
};

const STATUS_DOT = {
  IN_PROGRESS: 'bg-amber-400',
  ASSIGNED:    'bg-blue-400',
  COMPLETED:   'bg-emerald-400',
  CANCELLED:   'bg-gray-300',
};

export default function MessagesPage() {
  const { isRepManaged } = useInteractionMode();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [selectedSr, setSelectedSr]           = useState(null);
  const [loading, setLoading]                 = useState(true);

  useEffect(() => {
    serviceRequestAPI
      .getAll()
      .then(res => {
        const list = res.serviceRequests || [];
        setServiceRequests(list);
        if (list.length > 0) setSelectedSr(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Page header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Messages</h1>
          <p className="text-sm text-gray-500 mt-1">
            All your conversations with counselors in one place
          </p>
        </div>

        {loading ? (
          /* Loading skeleton */
          <div className="flex gap-4 h-[600px]">
            <div className="w-72 bg-white rounded-xl border border-gray-100 p-4 animate-pulse space-y-3 flex-shrink-0">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-gray-50 rounded-lg" />
              ))}
            </div>
            <div className="flex-1 bg-white rounded-xl border border-gray-100 animate-pulse" />
          </div>
        ) : serviceRequests.length === 0 ? (
          /* Empty state */
          <div className="text-center py-20 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-7 h-7 text-gray-300" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">No messages yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              Conversations with your counselors will appear here once a service request is assigned
            </p>
          </div>
        ) : (
          /* Two-panel layout */
          <div className="flex gap-4" style={{ height: 'calc(100vh - 240px)', minHeight: '480px' }}>

            {/* ── Left: conversation list ─────────────────────────── */}
            <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {serviceRequests.length} Conversation{serviceRequests.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
                {serviceRequests.map(sr => {
                  const label = SERVICE_TYPE_LABELS[sr.serviceType] || sr.serviceType || 'Service';
                  const counselor = sr.assignedCounselor;
                  const counselorName = counselor
                    ? `${counselor.firstName || ''} ${counselor.lastName || ''}`.trim()
                    : 'Awaiting Assignment';
                  const isSelected = selectedSr?.serviceRequestId === sr.serviceRequestId;
                  const dotColor = STATUS_DOT[sr.status] || 'bg-gray-300';

                  return (
                    <button
                      key={sr.serviceRequestId}
                      onClick={() => setSelectedSr(sr)}
                      className={`w-full text-left px-4 py-3.5 hover:bg-gray-50 transition-colors relative ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                    >
                      {isSelected && (
                        <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-[#1C8DEE] rounded-r-full" />
                      )}
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
                        <p className={`text-sm font-medium truncate ${
                          isSelected ? 'text-[#1C8DEE]' : 'text-gray-800'
                        }`}>
                          {label}
                        </p>
                      </div>
                      <p className="text-xs text-gray-400 truncate pl-3.5">{counselorName}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Right: chat panel ───────────────────────────────── */}
            <div className="flex-1 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {selectedSr ? (
                <ServiceChat
                  serviceRequestId={selectedSr.serviceRequestId}
                  counselor={selectedSr.assignedCounselor}
                  agent={selectedSr.assignedAgent}
                  isStaffView={false}
                  readOnly={isRepManaged}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                  <MessageSquare className="w-8 h-8 text-gray-200" />
                  <p className="text-sm">Select a conversation to start messaging</p>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
